import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { PlusCircle, Plane, Calendar, MapPin, Users, Wallet, Clock } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { format } from "date-fns";

interface NewTripDialogProps {
  onStartNewTrip: (tripName: string, budget: number) => void;
}

export const NewTripDialog = ({ onStartNewTrip }: NewTripDialogProps) => {
  const [tripName, setTripName] = useState("");
  const [budget, setBudget] = useState(5000);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [open, setOpen] = useState(false);

  // Real-time clock update
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleStartTrip = () => {
    if (tripName.trim()) {
      if (budget < 1000) {
        toast.warning("Consider setting a higher budget for better trip planning");
      }
      onStartNewTrip(tripName, budget);
      setOpen(false);
      setTripName("");
      setBudget(5000);
      toast.success(`Trip "${tripName}" created with a budget of ₹${budget}`);
    } else {
      toast.error("Please enter a trip name");
    }
  };

  const features = [
    { icon: Plane, label: "Travel Planning", color: "text-blue-500", bg: "bg-blue-100" },
    { icon: Calendar, label: "Trip Schedule", color: "text-purple-500", bg: "bg-purple-100" },
    { icon: MapPin, label: "Destinations", color: "text-green-500", bg: "bg-green-100" },
    { icon: Users, label: "Group Management", color: "text-orange-500", bg: "bg-orange-100" },
    { icon: Wallet, label: "Budget Tracking", color: "text-emerald-500", bg: "bg-emerald-100" },
    { icon: Clock, label: "Real-time Updates", color: "text-pink-500", bg: "bg-pink-100" },
  ];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <motion.div 
          whileHover={{ scale: 1.02 }} 
          whileTap={{ scale: 0.98 }}
          className="w-full"
        >
          <Button className="w-full bg-gradient-to-r from-primary to-secondary text-white h-auto py-6">
            <PlusCircle className="mr-2 h-5 w-5" />
            Start New Trip
          </Button>
        </motion.div>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">Start New Trip</DialogTitle>
          <p className="text-sm text-muted-foreground">
            {format(currentTime, "EEEE, MMMM do yyyy, h:mm:ss a")}
          </p>
        </DialogHeader>
        <div className="space-y-6 py-4">
          <div className="space-y-2">
            <Input
              placeholder="Enter trip name..."
              value={tripName}
              onChange={(e) => setTripName(e.target.value)}
              className="border-2 focus:border-primary"
            />
            <p className="text-sm text-muted-foreground">
              Give your trip a memorable name
            </p>
          </div>

          <div className="space-y-4">
            <label className="text-sm font-medium">
              Set Budget: ₹{budget.toLocaleString()}
            </label>
            <Slider
              value={[budget]}
              onValueChange={(value) => setBudget(value[0])}
              max={100000}
              min={1000}
              step={1000}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              Recommended budget: ₹5,000 - ₹50,000
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {features.map((feature, index) => (
              <motion.div
                key={feature.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-2">
                    <div className={`p-2 rounded-full ${feature.bg}`}>
                      <feature.icon className={`h-4 w-4 ${feature.color}`} />
                    </div>
                    <span className="text-sm font-medium">{feature.label}</span>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button 
            onClick={handleStartTrip} 
            className="w-full bg-gradient-to-r from-primary to-secondary hover:opacity-90 transition-opacity"
          >
            Start Adventure
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};